/**
 * React Query hooks for arable fields.
 * Contains query hooks for fetching arable field data.
 *
 * For mutations, see useArableFieldMutations.ts
 * For cache helpers, see arableFieldCacheHelpers.ts
 */

import { useQueries, useQuery } from "@tanstack/react-query";
import { queryKeys } from "common/queryKeys";
import API from "common/utils/Api";
import { fieldTileRefetchInterval } from "common/utils/tilePolling";
import { filterEntitiesForFarm } from "model/fieldHelpers";
import {
  ArableField,
  ArableFieldResponse,
  processArableFieldResponse,
} from "model/arableFieldTypes";
import { useMemo } from "react";
import { useAppStore } from "stores/useAppStore";
import { getArableFieldIdsByFarmId } from "./arableFieldCacheHelpers";

const arableFieldApi = new API<ArableFieldResponse>("fields");

// Arable fields are pre-fetched during initialization and rarely change.
// Use a long staleTime to prevent unnecessary re-fetches when components mount.
const ARABLE_FIELD_STALE_TIME = 5 * 60 * 1000; // 5 minutes

/**
 * React Query hook for fetching a single arable field by guid
 * @param guid - The arable field guid
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useArableField(
  guid: string | undefined,
  enabled = true,
  farmGuid?: string
) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const expectedFarmId = farmGuid ?? currentFarmId;

  const query = useQuery({
    queryKey: queryKeys.arableFields.detail(guid ?? ""),
    queryFn: async () => {
      if (!guid) {
        throw new Error("Arable field guid is required");
      }
      const response = await arableFieldApi.getOne(guid);
      const processed = processArableFieldResponse([response]);
      return processed[0];
    },
    enabled: enabled && !!guid,
    staleTime: ARABLE_FIELD_STALE_TIME,
    refetchInterval: (query) => fieldTileRefetchInterval(query.state.data),
  });

  const data = useMemo(() => {
    const field = query.data;
    if (!field || !expectedFarmId) return field;
    return field.farm === expectedFarmId ? field : undefined;
  }, [query.data, expectedFarmId]);

  return useMemo(() => ({ ...query, data }), [query, data]);
}

/**
 * React Query hook for fetching all arable fields for a farm
 * Fetches field IDs by farm ID first, then fetches each field individually
 * @param farmGuid - The farm guid
 */
export function useArableFields(farmGuid?: string) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const farmId = farmGuid || currentFarmId || "";

  // First, get the field IDs for this farm
  const fieldIdsQuery = useQuery({
    queryKey: queryKeys.arableFields.ids(farmId),
    queryFn: async () => {
      if (!farmId) {
        throw new Error("Farm guid is required");
      }
      return await getArableFieldIdsByFarmId(farmId);
    },
    enabled: !!farmId,
    staleTime: ARABLE_FIELD_STALE_TIME,
  });

  // Then, fetch each field individually
  const fieldQueries = useQueries({
    queries: (fieldIdsQuery.data || []).map((fieldId: string) => ({
      queryKey: queryKeys.arableFields.detail(fieldId),
      queryFn: async () => {
        const response = await arableFieldApi.getOne(fieldId);
        const processed = processArableFieldResponse([response]);
        return processed[0];
      },
      enabled: !!farmId && (fieldIdsQuery.data?.length || 0) > 0,
      staleTime: ARABLE_FIELD_STALE_TIME,
      refetchInterval: (query: { state: { data: ArableField | undefined } }) =>
        fieldTileRefetchInterval(query.state.data),
    })),
  });

  // Memoize fields array to prevent creating new references on every render
  const fields = useMemo(
    () =>
      filterEntitiesForFarm(
        fieldQueries
          .map((query) => query.data)
          .filter((field): field is ArableField => field !== undefined),
        farmId
      ),
    [fieldQueries, farmId]
  );

  const isLoading =
    fieldIdsQuery.isLoading || fieldQueries.some((query) => query.isLoading);
  const isError =
    fieldIdsQuery.isError || fieldQueries.some((query) => query.isError);

  // Memoize the return object to prevent unnecessary re-renders in consumers
  return useMemo(
    () => ({
      data: fields,
      isLoading,
      isError,
      isSuccess: !isLoading && !isError,
      fieldIdsQuery,
      queries: fieldQueries,
    }),
    [fields, isLoading, isError, fieldIdsQuery, fieldQueries]
  );
}

/**
 * React Query hook for fetching multiple arable fields by their guids
 * @param fieldIds - Array of arable field guids
 * @param enabled - Whether the query should be enabled (default: true)
 */
export function useArableFieldsByIds(
  fieldIds: string[] | undefined,
  enabled = true,
  farmGuid?: string
) {
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const expectedFarmId = farmGuid ?? currentFarmId;

  const queries = useQueries({
    queries: (fieldIds || []).map((fieldId) => ({
      queryKey: queryKeys.arableFields.detail(fieldId),
      queryFn: async () => {
        const response = await arableFieldApi.getOne(fieldId);
        const processed = processArableFieldResponse([response]);
        return processed[0];
      },
      enabled: enabled && fieldIds !== undefined && fieldIds.length > 0,
    })),
  });

  const fields = filterEntitiesForFarm(
    queries
      .map((query) => query.data)
      .filter((field): field is ArableField => field !== undefined),
    expectedFarmId
  );

  const isLoading = queries.some((query) => query.isLoading);
  const isError = queries.some((query) => query.isError);

  return {
    data: fields,
    isLoading,
    isError,
    queries,
  };
}

// Re-export mutations from useArableFieldMutations.ts
export {
  useCreateArableField,
  useDeleteArableField,
  useUpdateArableField,
} from "./useArableFieldMutations";

// Re-export cache helpers from arableFieldCacheHelpers.ts
export {
  changeArableFieldVisibilityQuery,
  fetchArableFieldsByFarmIdQuery,
  fetchArableFieldsByIdQuery,
  invalidateArableFieldDataQuery,
  resetArableFieldsQueryCache,
  useChangeArableFieldVisibilityQuery,
  useFetchArableFieldsByIdQuery,
  useInvalidateArableFieldDataQuery,
  useResetArableFieldsQueryCache,
  useValidateArableFieldDataQuery,
  validateArableFieldDataQuery,
} from "./arableFieldCacheHelpers";

// Export the API instance for direct use if needed
export { arableFieldApi };
